import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Select,
  Space,
  Table,
} from "antd";
import Cookies from "js-cookie";
import { useEffect, useMemo, useState } from "react";

import dayjs from "dayjs";
import {
  chucvuListURL,
  url_create_chucvu,
  url_update_chucvu,
  url_list_position_permission,
} from "../constants";
import { authAxios } from "../utils";

const { Search, TextArea } = Input;

export default function SettingsChucVu() {
  const [listChucVu, setListChucVu] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingChucVu, setEditingChucVu] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchListChucVu();
    fetchPermissions();
  }, []);

  const fetchListChucVu = async () => {
    try {
      setLoading(true);
      const res = await authAxios(Cookies.get("token")).get(chucvuListURL);
      setListChucVu(res.data || []);
    } catch (error) {
      console.error(error);
      message.error("Không thể tải danh sách chức vụ");
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissions = async () => {
    try {
      const res = await authAxios(Cookies.get("token")).get(
        url_list_position_permission,
      );
      setPermissions(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  // Filter theo keyword
  const dataSource = useMemo(() => {
    if (!keyword) return listChucVu;
    const lowerKeyword = keyword.toLowerCase();
    return listChucVu.filter(
      (item) =>
        item.name?.toLowerCase().includes(lowerKeyword) ||
        item.description?.toLowerCase().includes(lowerKeyword),
    );
  }, [listChucVu, keyword]);

  const handleOpenModal = (chucvu = null) => {
    setEditingChucVu(chucvu);
    if (chucvu) {
      form.setFieldsValue({
        name: chucvu.name,
        description: chucvu.description,
        is_active: chucvu.is_active,
        permission: chucvu.permission || [],
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ is_active: true, permission: [] });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingChucVu(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      if (editingChucVu) {
        await authAxios(Cookies.get("token")).put(
          `${url_update_chucvu}/${editingChucVu.id}`,
          values,
        );
        message.success("Cập nhật chức vụ thành công");
        window.dispatchEvent(new CustomEvent("refreshUserInfo"));
      } else {
        await authAxios(Cookies.get("token")).post(url_create_chucvu, values);
        message.success("Thêm chức vụ thành công");
      }
      handleCloseModal();
      fetchListChucVu();
    } catch (error) {
      console.error(error);
      message.error(
        editingChucVu ? "Không thể cập nhật chức vụ" : "Không thể tạo chức vụ",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const columns = [
    {
      title: "STT",
      key: "stt",
      width: 70,
      align: "center",
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: "Tên Chức vụ",
      dataIndex: "name",
      key: "name",
      align: "center",
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      align: "center",
      ellipsis: true,
    },
    {
      title: "Ngày tạo",
      dataIndex: "created",
      key: "created",
      align: "center",
      render: (text) => (text ? dayjs(text).format("DD/MM/YYYY") : "-"),
    },
    {
      title: "Trạng thái",
      dataIndex: "is_active",
      key: "is_active",
      align: "center",
      render: (isActive) => <span>{isActive ? "Kích hoạt" : "Khóa"}</span>,
    },
    {
      title: "Thao tác",
      key: "action",
      width: 100,
      align: "center",
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleOpenModal(record)}
          >
            Sửa
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col sm={14}>
          <Button type="primary" onClick={() => handleOpenModal()}>
            <PlusOutlined />
            Thêm chức vụ
          </Button>
        </Col>
        <Col sm={10}>
          <Search
            placeholder="Nhập từ khóa tìm kiếm"
            enterButton="Tìm kiếm"
            allowClear
            onSearch={(value) => {
              setKeyword(value.trim());
              setCurrentPage(1);
            }}
          />
        </Col>
      </Row>

      <Table
        rowKey="id"
        bordered
        loading={loading}
        dataSource={dataSource}
        columns={columns}
        onChange={handleTableChange}
        scroll={{ x: 1000, y: "70vh" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
          total: dataSource.length,
        }}
      />

      <Modal
        title={editingChucVu ? "Chỉnh sửa chức vụ" : "Thêm chức vụ mới"}
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={null}
        width={700}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            label="Tên chức vụ"
            name="name"
            rules={[{ required: true, message: "Vui lòng nhập tên chức vụ" }]}
          >
            <Input placeholder="Nhập tên chức vụ" />
          </Form.Item>

          <Form.Item label="Mô tả" name="description">
            <TextArea placeholder="Nhập mô tả" rows={4} />
          </Form.Item>

          <Form.Item label="Quyền hạn" name="permission">
            <Select
              mode="multiple"
              placeholder="Chọn quyền hạn"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={permissions.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            />
          </Form.Item>

          <Form.Item name="is_active" valuePropName="checked" label="Kích hoạt">
            <Checkbox>Kích hoạt</Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button onClick={handleCloseModal}>Hủy</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                {editingChucVu ? "Cập nhật" : "Thêm mới"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
